import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

var API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export var fetchBookCategories = createAsyncThunk(
  "products/fetchBookCategories",
  async function (language, thunkApi) {
    try {
      var response = await fetch(API_BASE_URL + "/book-categories", {
        method: "GET",
        signal: thunkApi.signal,
        headers: {
          Accept: "application/json",
          "Accept-Language": language || "en",
        },
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue(
          responseData.message || "Unable to retrieve book categories"
        );
      }

      return Array.isArray(responseData.data)
        ? responseData.data
        : [];
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.message || "Unable to connect to the server"
      );
    }
  }
);

export var fetchBooks = createAsyncThunk(
  "products/fetchBooks",
  async function (language, thunkApi) {
    try {
      var headers = {
        Accept: "application/json",
        "Accept-Language": language || "en",
      };

      var firstResponse = await fetch(API_BASE_URL + "/books?page=1", {
        method: "GET",
        signal: thunkApi.signal,
        headers: headers,
      });

      var firstData = await firstResponse.json();

      if (!firstResponse.ok) {
        return thunkApi.rejectWithValue(
          firstData.message || "Unable to retrieve books"
        );
      }

      var allBooks = Array.isArray(firstData.data) ? firstData.data : [];
      var pagination = firstData.pagination || null;
      var lastPage = pagination && pagination.last_page ? Number(pagination.last_page) : 1;

      if (lastPage > 1) {
        var pageNumbers = [];
        for (var p = 2; p <= lastPage; p++) {
          pageNumbers.push(p);
        }

        var remainingResponses = await Promise.all(
          pageNumbers.map(function (page) {
            return fetch(API_BASE_URL + "/books?page=" + page, {
              method: "GET",
              signal: thunkApi.signal,
              headers: headers,
            }).then(function (res) { return res.json(); });
          })
        );

        remainingResponses.forEach(function (pageData) {
          if (Array.isArray(pageData.data)) {
            allBooks = allBooks.concat(pageData.data);
          }
          if (pageData.pagination) {
            pagination = pageData.pagination;
          }
        });
      }

      return {
        books: allBooks,
        pagination: pagination,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.message || "Unable to connect to the server"
      );
    }
  }
);

export var fetchBookById = createAsyncThunk(
  "products/fetchBookById",
  async function (payload, thunkApi) {
    try {
      var bookId = payload.bookId;
      var language = payload.language || "en";

      var response = await fetch(
        API_BASE_URL + "/books/" + encodeURIComponent(bookId),
        {
          method: "GET",
          signal: thunkApi.signal,
          headers: {
            Accept: "application/json",
            "Accept-Language": language,
          },
        }
      );

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue(
          responseData.message || "Unable to retrieve book"
        );
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.message || "Unable to connect to the server"
      );
    }
  }
);

var initialState = {
  categories: [],
  books: [],
  pagination: null,
  selectedBook: null,

  categoriesLoading: false,
  booksLoading: false,
  detailsLoading: false,

  categoriesError: "",
  booksError: "",
  detailsError: "",
};

var productsSlice = createSlice({
  name: "products",
  initialState: initialState,
  reducers: {},
  extraReducers: function (builder) {
    builder
      .addCase(fetchBookCategories.pending, function (state) {
        state.categoriesLoading = true;
        state.categoriesError = "";
      })
      .addCase(fetchBookCategories.fulfilled, function (state, action) {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchBookCategories.rejected, function (state, action) {
        state.categoriesLoading = false;
        state.categoriesError =
          action.payload || "Unable to retrieve book categories";
      })

      .addCase(fetchBooks.pending, function (state) {
        state.booksLoading = true;
        state.booksError = "";
      })
      .addCase(fetchBooks.fulfilled, function (state, action) {
        state.booksLoading = false;
        state.books = action.payload.books;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBooks.rejected, function (state, action) {
        state.booksLoading = false;
        state.booksError =
          action.payload || "Unable to retrieve books";
      })

      .addCase(fetchBookById.pending, function (state) {
        state.detailsLoading = true;
        state.detailsError = "";
        state.selectedBook = null;
      })
      .addCase(fetchBookById.fulfilled, function (state, action) {
        state.detailsLoading = false;
        state.selectedBook = action.payload;
      })
      .addCase(fetchBookById.rejected, function (state, action) {
        state.detailsLoading = false;
        state.detailsError =
          action.payload || "Unable to retrieve book";
      });
  },
});

export default productsSlice.reducer;
